import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminPDFUpload from "@/components/AdminPDFUpload";
import { Pagination } from "@/components/Pagination";
import { getUserData, pdfAPI } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, FileText, Trash2, CheckCircle, XCircle, Upload, List, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { translateStrengthName } from "@/lib/strength-i18n";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface PDFReport {
  id: string;
  targetUserId?: string;
  user: string;
  email: string;
  fileName: string;
  storedFileName?: string;
  fileUrl?: string;
  uploadDate: string;
  fileSize: number;
  status: "processing" | "completed" | "failed";
  strengthsCount: number;
}

interface PDFReportDetail extends PDFReport {
  extractedData?: {
    username?: string;
    domains?: { domainName: string }[];
    strengths?: { rank: number; strengthName: string; theme_name?: string }[];
  };
}

export default function AdminPDFReports() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const targetUserFilter = searchParams.get("userId");
  const currentUser = getUserData();
  const isAdmin = currentUser?.role === "admin";
  const [searchQuery, setSearchQuery] = useState("");
  const [reports, setReports] = useState<PDFReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<PDFReportDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(targetUserFilter ? "reports" : "upload");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const itemsPerPage = 5;

  useEffect(() => {
    if (!isAdmin) return;

    loadReports();
  }, [currentPage, isAdmin, targetUserFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setActiveTab(targetUserFilter ? "reports" : "upload");
    setSelectedReport(null);
    setIsDetailOpen(false);
  }, [targetUserFilter]);

  const loadReports = async () => {
    if (!isAdmin) return;

    setLoading(true);
    try {
      const response = await pdfAPI.getHistory(currentPage, itemsPerPage, targetUserFilter);
      if (response.success && response.data) {
        setReports(response.data);
        setTotalCount(response.count || 0);
      }
    } catch (error: any) {
      console.error("Error loading PDF reports:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "pdfReports.loadFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const filteredReports = reports.filter(
    (report) => {
      const matchesTargetUser = !targetUserFilter || report.targetUserId === targetUserFilter;
      const matchesSearch =
        report.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.fileName.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesTargetUser && matchesSearch;
    }
  );

  const handleViewReport = async (reportId: string) => {
    if (!isAdmin) return;

    setLoadingReportId(reportId);
    try {
      const response = await pdfAPI.getReport(reportId);
      if (response.success && response.data) {
        setSelectedReport({
          ...response.data,
          user: response.data.targetUser?.name || response.data.username,
          email: response.data.targetUser?.email || response.data.userEmail || "",
          uploadDate: response.data.createdAt,
        });
        setIsDetailOpen(true);
      }
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "pdfReports.extractionLoadFailed"),
        variant: "destructive",
      });
    } finally {
      setLoadingReportId(null);
    }
  };

  useEffect(() => {
    if (!targetUserFilter || loading || loadingReportId) return;

    const existingReport = filteredReports[0];

    if (existingReport) {
      setActiveTab("reports");
      if (selectedReport?.id !== existingReport.id) {
        handleViewReport(existingReport.id);
      }
    } else {
      setActiveTab("upload");
      setSelectedReport(null);
      setIsDetailOpen(false);
    }
  }, [targetUserFilter, loading, filteredReports, loadingReportId, selectedReport?.id]);

  const handleDeleteReport = async (reportId: string) => {
    if (!isAdmin) return;

    setDeletingReportId(reportId);
    try {
      const response = await pdfAPI.deleteReport(reportId);
      if (response.success) {
        toast({
          title: t('pdfReports.reportDeleted'),
          description: t('pdfReports.reportDeletedDesc'),
        });
        // Reload reports after deletion
        loadReports();
      }
    } catch (error: any) {
      console.error("Error deleting report:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "pdfReports.deleteReportFailed"),
        variant: "destructive",
      });
    } finally {
      setDeletingReportId(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-success" />;
      case "processing":
        return <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-error" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="status-success">{t('pdfReports.statusCompleted')}</Badge>;
      case "processing":
        return <Badge className="bg-primary/10 text-primary border-primary/25">{t('pdfReports.statusProcessing')}</Badge>;
      case "failed":
        return <Badge className="status-error">{t('pdfReports.statusFailed')}</Badge>;
      default:
        return <Badge>{t('pdfReports.statusUnknown')}</Badge>;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return t("pdfReports.zeroBytes");
    const k = 1024;
    const sizes = [t("pdfReports.bytes"), t("pdfReports.kb"), t("pdfReports.mb"), t("pdfReports.gb")];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const renderReportHeaderCells = () => {
    const cells = [
      <TableHead key="user">{t('pdfReports.user')}</TableHead>,
      <TableHead key="fileName">{t('pdfReports.fileName')}</TableHead>,
      <TableHead key="uploadDate">{t('pdfReports.uploadDate')}</TableHead>,
      <TableHead key="fileSize">{t('pdfReports.fileSize')}</TableHead>,
      <TableHead key="status">{t('pdfReports.status')}</TableHead>,
      <TableHead key="strengths">{t('pdfReports.strengths')}</TableHead>,
      <TableHead key="actions">{t('pdfReports.actions')}</TableHead>,
    ];

    return isRtl ? [...cells].reverse() : cells;
  };

  const renderReportRowCells = (report: PDFReport) => {
    const cells = [
      <TableCell key="user" dir={isRtl ? "rtl" : "ltr"}>
        <div className="min-w-0">
          <div className="break-words font-medium">{report.user}</div>
          {report.email && (
            <div className="break-all text-sm text-muted-foreground" dir="ltr">{report.email}</div>
          )}
        </div>
      </TableCell>,
      <TableCell key="fileName" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="break-all text-sm" dir="ltr">{report.fileName}</span>
        </div>
      </TableCell>,
      <TableCell key="uploadDate" dir="ltr">
        {new Date(report.uploadDate).toLocaleDateString()} <br />
        <span className="text-xs text-muted-foreground">
          {new Date(report.uploadDate).toLocaleTimeString()}
        </span>
      </TableCell>,
      <TableCell key="fileSize" dir={isRtl ? "rtl" : "ltr"}>{formatFileSize(report.fileSize)}</TableCell>,
      <TableCell key="status" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex items-center gap-2">
          {getStatusIcon(report.status)}
          {getStatusBadge(report.status)}
        </div>
      </TableCell>,
      <TableCell key="strengths" dir={isRtl ? "rtl" : "ltr"}>
        {report.status === "completed" && report.strengthsCount > 0 ? (
          <span className="text-sm font-medium">
            {t('pdfReports.strengthsCount', { count: report.strengthsCount })}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )}
      </TableCell>,
      <TableCell key="actions" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewReport(report.id)}
            disabled={loadingReportId === report.id}
            title={t("pdfReports.extractionTitle")}
          >
            {loadingReportId === report.id ? (
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteReport(report.id)}
            disabled={deletingReportId === report.id}
          >
            {deletingReportId === report.id ? (
              <div className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 text-error" />
            )}
          </Button>
        </div>
      </TableCell>,
    ];

    return isRtl ? [...cells].reverse() : cells;
  };

  return (
    <div className="min-w-0 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div>
        <h2 className="text-xl font-bold mb-2 sm:text-2xl">{t('pdfReports.title')}</h2>
        <p className="text-muted-foreground">
          {t('pdfReports.description')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0 space-y-6">
        <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
        <TabsList className="w-max bg-card/50 backdrop-blur-sm border border-border/50">
          <TabsTrigger value="upload" className="gap-2">
            <Upload className="w-4 h-4" />
            {t('pdfReports.uploadNew')}
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <List className="w-4 h-4" />
            {t('pdfReports.allReports')} ({totalCount})
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="upload">
          <AdminPDFUpload onUploadComplete={loadReports} initialTargetUserId={targetUserFilter} />
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('pdfReports.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <Table className={`min-w-[860px] ${isRtl ? "admin-pdf-reports-rtl-table" : ""}`} dir="ltr">
          <TableHeader>
            <TableRow>
              {renderReportHeaderCells()}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-muted-foreground">{t('pdfReports.loadingReports')}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {t('pdfReports.noReportsFound')}
                </TableCell>
              </TableRow>
            ) : (
              filteredReports.map((report) => (
                <TableRow key={report.id}>
                  {renderReportRowCells(report)}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="mt-4"
        />
      )}
        </TabsContent>
      </Tabs>

      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <DialogHeader>
            <DialogTitle>{t("pdfReports.extractionTitle")}</DialogTitle>
            <DialogDescription>
              {selectedReport?.user}
              {selectedReport?.email ? ` (${selectedReport.email})` : ""}
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-5">
              <Card className="p-4 bg-muted/50 border-border">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("pdfReports.file")}</p>
                    <p className="font-medium break-all">{selectedReport.fileName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("pdfReports.status")}</p>
                    <div className="mt-1">{getStatusBadge(selectedReport.status)}</div>
                  </div>
                </div>
              </Card>

              <div>
                <h3 className="font-semibold mb-3">{t("pdfReports.extractedStrengths")}</h3>
                <div className="max-h-[400px] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {(selectedReport.extractedData?.strengths || []).map((strength) => (
                      <div
                        key={`${strength.rank}-${strength.strengthName}`}
                        className="flex min-w-0 items-center gap-3 p-2 bg-muted/30 rounded border border-border/50"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold">{strength.rank}</span>
                        </div>
                        <p className="min-w-0 break-words text-sm font-medium">
                          {translateStrengthName(strength.strengthName || strength.theme_name, t, i18n.language)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
